import Form from "react-bootstrap/Form";
import Button from "react-bootstrap/Button";
import React, { useState, useEffect, useMemo, useRef } from "react";
import apiService from "../services/api.service";
import ReCAPTCHA from "react-google-recaptcha";
import { CAPTCHA_SITEKEY } from "../config";
import { Validator as CFValidator } from "@marketto/codice-fiscale-utils/dist/codice-fiscale-utils";
import { useHistory, Link, useLocation } from "react-router-dom";
import PasswordToggle from "../Components/PasswordToggle";
import FlashAlert from "../Components/FlashAlert/FlashAlert";
import RegistrationFieldsInput from "../Components/RegistrationFieldsInput";
import { useApplication } from "../context/ApplicationContext";
import { areAnswersValid, initialValues } from "../utils/registrationFields";
import { appPath } from "../utils/appSlug";
import "./login.css";

const CreateUser = ({ appSlug, adminMode = false, embedded = false, eventUid, eventName }) => {
  const [username, setUsername] = useState("");
  const [usernameConfirm, setUsernameConfirm] = useState("");	
  const [password, setPassword] = useState("");
  const [passwordConfirm, setPasswordConfirm] = useState("");
  const [firstname, setFirstname] = useState("");
  const [lastname, setLastname] = useState("");
  const [successfulRegister, setSuccessfulRegister] = useState(true);
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [captchaKey, setCaptchaKey] = useState("")
  const [captchaCompleted, setCaptchaComplete] = useState(false);
  const [codiceFiscale, setCodiceFiscale] = useState("");
  const [buttonDisabled, setButtonDisabled] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [applications, setApplications] = useState([]);
  const [applicationId, setApplicationId] = useState("");
  const [applicationsLoading, setApplicationsLoading] = useState(true);
  const [applicationsError, setApplicationsError] = useState("");
  const role = "utente_base";
  const [extraValues, setExtraValues] = useState({});
  const captchaRef = useRef(null);
  const extraFieldsKeyRef = useRef("");

  let history = useHistory()
  let location = useLocation();
  const captchaEnabled = Boolean(CAPTCHA_SITEKEY);
  const isAdmin = (adminMode || Boolean(location.state?.isAdmin)) && !appSlug;
  const { application, loading: appLoading } = useApplication();
  const extraFields = useMemo(
    () => (isAdmin ? [] : application?.registration_fields || []),
    [isAdmin, application]
  );

  useEffect(() => {
    if (!isAdmin) return;
    let active = true;
    setApplicationsLoading(true);
    apiService.getApplications().then(({ data }) => {
      if (active) setApplications(data || []);
    }).catch(() => {
      if (active) setApplicationsError("Impossibile caricare le applicazioni. Ricarica la pagina.");
    }).finally(() => { if (active) setApplicationsLoading(false); });
    return () => { active = false; };
  }, [isAdmin]);

  const validApplication = applications.some((item) => String(item.id) === applicationId);

  useEffect(() => {
    if (isAdmin) return;
    const key = (extraFields || []).map((field) => field.id).join("|");
    if (key === extraFieldsKeyRef.current) return;
    extraFieldsKeyRef.current = key;
    setExtraValues(initialValues(extraFields));
  }, [isAdmin, extraFields]);

  useEffect(() => {
    if (!captchaEnabled || isAdmin) {
      setCaptchaComplete(true);
    }
  }, [captchaEnabled, isAdmin]);

  const resetCaptchaOnly = () => {
    setCaptchaKey("");
    setCaptchaComplete(!captchaEnabled);
    if (captchaRef.current && typeof captchaRef.current.reset === "function") {
      captchaRef.current.reset();
    }
  };

  const checkPasswordMismatch = () => {
    if (passwordConfirm === "") {
      if (password !== "") {
        return true
      }
    }
    if (passwordConfirm === password) {
      return false
    } else {
      return true;
    }
  }

  const checkUsernameMismatch = () => {
    if (usernameConfirm === "") {
      if (username !== "") {
        return true
      }
    }
    if (usernameConfirm === username) {
      return false
    } else {
      return true;
    }
  }

  const checkCF = () => {
    return CFValidator.codiceFiscale(codiceFiscale).valid
  }
  const onPasswordChange = (e) => {
    setPassword(e.target.value);
  };

  const onUsernameChange = (e) => {
    setUsername(e.target.value);
  };

  const onUsernameConfirmChange = (e) => {
    setUsernameConfirm(e.target.value);
  };

  const onPasswordConfirmChange = (e) => {
    setPasswordConfirm(e.target.value);
  };
  const onfirstnameChange = (e) => {
    setFirstname(e.target.value);
  };
  const onLastNameChange = (e) => {
    setLastname(e.target.value);
  };
  const onCodiceFiscaleChange = (e) => {
    setCodiceFiscale(e.target.value.toUpperCase());
  };

  const onCaptchaChange = (value) => {
    setCaptchaKey(value || "");
    setCaptchaComplete(Boolean(value));
  };

  const handleSubmit = async (e) => {
    setButtonDisabled(true);
    setErrorMessage("");
    setFormSubmitted(false);
    e.preventDefault();
    if (isAdmin) {
      if (!validApplication || applicationsLoading) {
        setFormSubmitted(true);
        setSuccessfulRegister(false);
        setButtonDisabled(false);
        setErrorMessage("Seleziona un'applicazione per il nuovo utente.");
        return;
      }
      try {
        await apiService.createUser_Admin(
          username,
          password,
          firstname,
          lastname,
          codiceFiscale,
          role,
          Number(applicationId)
        );
        history.push(embedded ? "/dashboard/utenti" : "/users", {
          successReason: "Utente aggiunto con successo",
        });
      } catch (error) {
        console.log(error);
        setFormSubmitted(true);
        setSuccessfulRegister(false);
        setButtonDisabled(false);
        if (error.response && error.response.data.detail) {
          if (error.response.data.detail === 'Username already exists') {
            setErrorMessage("Questa email risulta già registrata");
          }
        }
      }
      return;
    }

    if (!appSlug) {
      setFormSubmitted(true);
      setSuccessfulRegister(false);
      setButtonDisabled(false);
      setErrorMessage("La registrazione è disponibile solo dal sito dell'applicazione.");
      return;
    }

    try {
      await apiService.createUser_guest({
        username,
        password,
        firstname,
        lastname,
        captchaKey: captchaEnabled ? captchaKey : "",
        applicationSlug: appSlug,
        customFields: extraValues,
        eventUid,
      });
      const loginPath = appPath(appSlug, 'login') + (eventUid ? `?event=${encodeURIComponent(eventUid)}` : '');
      history.push(loginPath, {
        successReason:
          "La registrazione è avvenuta con successo, controlla la tua email e segui le istruzioni ricevute per attivare il tuo account.",
      });
    } catch (error) {
      console.log(error);
      setFormSubmitted(true);
      setSuccessfulRegister(false);
      setButtonDisabled(false);

      const detail = error.response?.data?.detail;
      if (detail === "Username already exists") {
        setErrorMessage(
          "Questa email risulta già registrata, se non riesci ad effettuare l'accesso con i tuoi dati, contatta infoceformedsrl@gmail.com"
        );
      } else if (detail === "CAPTCHA Invalid") {
        setErrorMessage("Verifica anti-spam non valida. Completa il reCAPTCHA e riprova.");
        resetCaptchaOnly();
      } else if (typeof detail === "string" && detail.trim()) {
        setErrorMessage(detail);
        if (captchaEnabled) resetCaptchaOnly();
      } else {
        setErrorMessage("Errore durante la registrazione.");
        if (captchaEnabled) resetCaptchaOnly();
      }
    }
  };

  const showCaptcha = () => {
    if (isAdmin || !captchaEnabled) {
      return null;
    }
    return (
      <Form.Group className="mb-3" controlId="formReCaptcha">
        <ReCAPTCHA
          ref={captchaRef}
          sitekey={CAPTCHA_SITEKEY}
          onChange={onCaptchaChange}
          onExpired={resetCaptchaOnly}
          hl="it"
        />
      </Form.Group>
    );
  }

  const displayAlert = (e) => {
    if (formSubmitted) {
      if (successfulRegister) {
        return (
          <FlashAlert variant="success">
            La registrazione è avvenuta con successo, controlla la tua email e segui le istruzioni ricevute per attivare il tuo account.
          </FlashAlert>
        );
      }
      else {
        if(errorMessage!== ""){
          let msg = errorMessage;
        return (
          <FlashAlert variant="danger">
            {msg}
          </FlashAlert>
        );
        }
        else{
          return (
            <FlashAlert variant="danger">
              Errore durante la registrazione.
            </FlashAlert>
          );
        }
      }
    }
  };

  return (
    <div className={`login-page${!appSlug ? " login-page--platform" : ""}${embedded ? " login-page--embedded" : ""}`}>
      <div className="login-card login-card--wide">
        {displayAlert()}
        <p className="login-kicker">Area riservata</p>
        <h1 className="login-title">{isAdmin ? "Aggiungi utente" : "Registrazione"}</h1>
        <p className="login-subtitle">
          {isAdmin
            ? "Crea un nuovo utente dalla console amministratore."
            : "Inserisci i tuoi dati per registrarti alla piattaforma webinar."}
        </p>
        {eventName && <p className="login-subtitle">Iscrizione a: <strong>{eventName}</strong></p>}
        <Form onSubmit={handleSubmit} className="login-form">
          {isAdmin && (
            <Form.Group className="mb-3" controlId="createUserApplication">
              <Form.Label>Applicazione</Form.Label>
              <Form.Select required value={applicationId} onChange={(e) => setApplicationId(e.target.value)} disabled={applicationsLoading}>
                <option value="">{applicationsLoading ? "Caricamento applicazioni..." : "Seleziona applicazione"}</option>
                {applications.map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}
              </Form.Select>
              {applicationsError && <FlashAlert variant="danger">{applicationsError}</FlashAlert>}
            </Form.Group>
          )}
          <Form.Group className="mb-3" controlId="formBasicFirstName">
            <Form.Label>Nome</Form.Label>
            <Form.Control
              required
              type="text"
              placeholder="Inserisci nome"
              autoComplete="given-name"
              value={firstname}
              onChange={onfirstnameChange}
            />
          </Form.Group>
          <Form.Group className="mb-3" controlId="formBasicLastName">
            <Form.Label>Cognome</Form.Label>
            <Form.Control
              required
              type="text"
              autoComplete="family-name"
              placeholder="Inserisci cognome"
              value={lastname}
              onChange={onLastNameChange}
            />
          </Form.Group>
          <Form.Group className="mb-3" controlId="em1">
            <Form.Label>Email</Form.Label>
            <Form.Control
              required
              type="email"
              placeholder="Inserisci email"
              autoComplete="email"
              value={username}
              onChange={onUsernameChange}
            />
            <div className="login-form-note">
              Non usare email PEC in quanto non abilitate alla ricezione di posta ordinaria.
            </div>
          </Form.Group>
          <Form.Group className="mb-3" controlId="em2">
            <Form.Label>Conferma email</Form.Label>
            <Form.Control
              required
              type="email"
              placeholder="Inserisci nuovamente l'email"
              autoComplete="email"
              value={usernameConfirm}
              onChange={onUsernameConfirmChange}
              isValid={usernameConfirm === "" ? false : !checkUsernameMismatch()}
              isInvalid={checkUsernameMismatch()}
              onPaste={(e) => { e.preventDefault() }}
            />
            <Form.Control.Feedback type="invalid">
              Le email non corrispondono
            </Form.Control.Feedback>
          </Form.Group>
          <Form.Group className="mb-3" controlId="ps1">
            <Form.Label>Password</Form.Label>
            <PasswordToggle
              value={password}
              onChange={onPasswordChange}
              placeholder="Inserisci la password"
            />
          </Form.Group>
          <Form.Group className="mb-3" controlId="ps2">
            <Form.Label>Conferma password</Form.Label>
            <PasswordToggle
              value={passwordConfirm}
              onChange={onPasswordConfirmChange}
              placeholder="Inserisci nuovamente la password"
              isValid={password === "" ? false : !checkPasswordMismatch()}
              isInvalid={checkPasswordMismatch()}
            />
            <Form.Control.Feedback type="invalid">
              Le password non corrispondono
            </Form.Control.Feedback>
          </Form.Group>
          {isAdmin ? (
            <>
          <Form.Group className="mb-3" controlId="formBasicCodiceFiscale">
            <Form.Label>Codice fiscale</Form.Label>
            <Form.Control
              required
              type="text"
              placeholder="Inserisci il tuo codice fiscale"
              value={codiceFiscale}
              autoComplete="off"
              onChange={onCodiceFiscaleChange}
              style={{ textTransform: "uppercase" }}
              isValid={codiceFiscale === "" ? false : checkCF()}
              isInvalid={codiceFiscale === "" ? false : !checkCF()}
            />
            <Form.Control.Feedback type="invalid">
              Codice fiscale non valido
            </Form.Control.Feedback>
          </Form.Group>
            </>
          ) : appLoading ? (
            <p>Caricamento campi di iscrizione...</p>
          ) : (
            <RegistrationFieldsInput
              fields={extraFields}
              values={extraValues}
              onChange={setExtraValues}
            />
          )}
          {isAdmin && (
            <Form.Group className="mb-3" controlId="formUserRole">
              <Form.Label>Ruolo</Form.Label>
              <Form.Control type="text" value="Utente" disabled readOnly />
            </Form.Group>
          )}
          {showCaptcha()}
          <Button
            className="login-submit"
            size="lg"
            variant="primary"
            type="submit"
            disabled={
              checkPasswordMismatch()
              || checkUsernameMismatch()
              || (!isAdmin && captchaEnabled && !captchaCompleted)
              || (isAdmin && !checkCF())
              || (isAdmin && (applicationsLoading || !validApplication))
              || (!isAdmin && (appLoading || !areAnswersValid(extraFields, extraValues, {
                codiceFiscaleValid: (value) => CFValidator.codiceFiscale(value).valid,
              })))
              || buttonDisabled
            }>
            {isAdmin ? "Aggiungi utente" : "Registrati"}
          </Button>
        </Form>
        {!isAdmin && (
        <div className="login-register-box">
          <p className="login-register-prompt">
            Hai già un account? <Link to={appPath(appSlug, "login") + (eventUid ? `?event=${encodeURIComponent(eventUid)}` : '')}>Accedi</Link>
          </p>
        </div>
        )}
      </div>
    </div>
  );
};

export default CreateUser;
